import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Container, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useFinancialContexts } from '../contexts/ContextsContext';
import { safeNext } from '../utils/safeNext';

/**
 * Where the bot's "Open the app" button lands: the token in the URL becomes a
 * session and the person goes straight to their dashboard, or to the place
 * the link names ("Ver meu painel" opens the group with their expense). The
 * link works once, so a refresh here shows the way back to a fresh one.
 */
const TelegramLoginPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { loginWithToken } = useAuth();
  const { selectContext } = useFinancialContexts();
  const [failed, setFailed] = useState(false);
  const attempted = useRef(false);

  useEffect(() => {
    const token = params.get('token');
    if (attempted.current) return;
    attempted.current = true;

    if (!token) {
      setFailed(true);
      return;
    }
    loginWithToken(token)
      .then(() => {
        // "view=personal": a link from the private chat is about the personal
        // side, whatever group was in view on this browser.
        const next = new URL(safeNext(params.get('next')), window.location.origin);
        if (next.searchParams.get('view') === 'personal') {
          selectContext(undefined);
          next.searchParams.delete('view');
        }
        navigate(`${next.pathname}${next.search}${next.hash}`, { replace: true });
      })
      .catch(() => setFailed(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container maxWidth="sm">
      <Box sx={{ minHeight: '70vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, textAlign: 'center' }}>
        {failed ? (
          <>
            <Typography variant="h5">{t('telegramLogin.failed')}</Typography>
            <Typography color="text.secondary">{t('telegramLogin.failedHint')}</Typography>
            <Button variant="contained" onClick={() => navigate('/login')}>
              {t('telegramLogin.goToLogin')}
            </Button>
          </>
        ) : (
          <>
            <CircularProgress />
            <Typography color="text.secondary">{t('telegramLogin.title')}</Typography>
          </>
        )}
      </Box>
    </Container>
  );
};

export default TelegramLoginPage;
